import * as React from 'react'
import { FC, useCallback, useMemo } from 'react'
import TableWrapper from '../../../../../components/table-wrapper/table-wrapper'
import { Pagination, Space, Table } from 'antd'
import { useSearchParams } from 'react-router-dom'
import { columns } from './constants/columns'
import useEndpointQueryParams, {
    DeserializeFilters,
} from '../../../../../hooks/table-pages/use-endpoint-query-parameters'
import { EndpointQueryParameters } from '../../../../../types/strapi/strapi'
import useTablePagination from '../../../../../hooks/table-pages/use-table-pagination'
import Receipt, { ReceiptStatus } from '../../../../../types/api/receipt'
import { useGetReceiptsQuery } from '../../../../../store/api/receipts-api'
import ReceiptPosition from '../../../../../types/api/receipt-position'
import FiltersForm from './components/filters-form/filters-form'
import qs from 'qs'
import FormValues from './components/filters-form/types/form-values'
import { buildFilters } from './helpers/build-filters'
import filterReceiptPositionsByForm from '../../../../../helpers/filters/filter-receipt-positions-by-form'
import Edit from './components/edit/edit'
import Accept from './components/accept/accept'

export type DataSourceItem = ReceiptPosition & { rowSpan: number; key: string }

interface ReceiptsProps {
    actions?: boolean
    receiptStatusFilters?: ReceiptStatus[]
    sort?: string[]
}

const Receipts: FC<ReceiptsProps> = ({
    receiptStatusFilters,
    sort = ['createdAt:desc'],
    actions = false,
}) => {
    const [searchParams] = useSearchParams()
    const deserializeFilters: DeserializeFilters = filtersString => {
        const formValues = qs.parse(filtersString) as Partial<FormValues>
        return buildFilters(formValues, receiptStatusFilters)
    }

    const params = useEndpointQueryParams({ deserializeFilters })
    const receiptsParams: EndpointQueryParameters = {
        populate: {
            supplier: true,
            receipt_positions: {
                populate: {
                    material: { populate: '*' },
                    initiator: true,
                    unit: true,
                },
            },
        },
        pagination: params.pagination,
        filters: params.filters,
        sort: sort,
    }

    const { data, isLoading } = useGetReceiptsQuery(receiptsParams)
    const pagination = useTablePagination(params, data)
    const receipts: Receipt[] = data?.data || []

    const formValues = useMemo(
        () => qs.parse(searchParams.get('filters') || '') as Partial<FormValues>,
        [searchParams]
    )

    const dataSource: DataSourceItem[] = receipts.flatMap(s => {
        const positions = filterReceiptPositionsByForm(s.receipt_positions || [], formValues)
        if (positions.length === 0) {
            return []
        }

        return positions.map((pos, index) => ({
            ...pos,
            key: `${s.id}-${pos.id}`,
            receiptCreatedAt: s.createdAt,
            receiptId: s.id,
            receiptDocumentId: s.documentId,
            receiptEstimatedDate: s.estimatedDate,
            supplier: s.supplier,
            receiptStatus: s.receiptStatus,
            rowSpan: index === 0 ? positions.length : 0,
        }))
    })
    const getColums = () => {
        const newColumns = [...columns]
        if (actions) {
            newColumns.push({
                title: 'Действия',
                fixed: 'right',
                width: 80,
                render: record => {
                    return (
                        <Space>
                            <Edit documentId={record.receiptDocumentId} />
                            <Accept documentId={record.receiptDocumentId} />
                        </Space>
                    )
                },
                onCell: v => ({
                    style: { verticalAlign: 'top' },
                    rowSpan: v.rowSpan,
                }),
            })
        } else {
            newColumns.push({
                title: 'Действия',
                fixed: 'right',
                width: 80,
            })
        }
        return newColumns
    }
    const columnsData = getColums()

    const tableFooter = useCallback(
        () => <Pagination {...pagination} size='small' align='end' />,
        [pagination]
    )

    return (
        <TableWrapper style={{ flex: 'auto' }} currentTableHeight={75}>
            <Table
                bordered
                columns={columnsData}
                dataSource={dataSource}
                pagination={false}
                footer={tableFooter}
                loading={isLoading}
                rowKey={'documentId'}
                summary={() => (
                    <Table.Summary fixed={'top'}>
                        <FiltersForm />
                    </Table.Summary>
                )}
            />
        </TableWrapper>
    )
}

export default Receipts
